import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Globe, ShoppingBag, Zap, ExternalLink, ChevronDown, ChevronRight, Info } from "lucide-react";

const HELP = {
  wordpress: {
    title: "How to get your WooCommerce API keys",
    admin_path: "/wp-admin/admin.php?page=wc-settings&tab=advanced&section=keys",
    admin_label: "Open WooCommerce → REST API",
    steps: [
      "Log in to your WordPress admin (you must have administrator role).",
      "Go to WooCommerce → Settings → Advanced → REST API → Add Key.",
      "Description: \"HelloSir.ai\". User: yourself. Permissions: Read (or Read/Write if you want add-to-cart).",
      "Click Generate API key. Copy the Consumer Key (ck_…) and Consumer Secret (cs_…) shown ONCE.",
      "Paste them below. Keep the secret safe — it cannot be re-displayed.",
    ],
    note: "Requires WooCommerce 3.5+ and HTTPS. If you don't see the REST API tab, install/activate WooCommerce first.",
  },
  shopify: {
    title: "How to get your Shopify Storefront API token",
    admin_path: "/admin/apps",
    admin_label: "Open Shopify → Apps",
    steps: [
      "Shopify Admin → Settings → Apps and sales channels → Develop apps (enable if asked).",
      "Click Create an app → name it \"HelloSir.ai\" → Create.",
      "Open Configuration → Storefront API access scopes → Configure.",
      "Enable: unauthenticated_read_product_listings, _read_product_inventory, _read_product_pickup_locations, _read_product_tags.",
      "Save → API credentials tab → Install app → copy the Storefront API access token (shpat_…).",
    ],
    note: "The Storefront token is public-safe (read-only). It powers product search; no customer data exposed.",
  },
  magento: {
    title: "How to create a Magento 2 Integration token",
    admin_path: "/admin/admin/integration/",
    admin_label: "Open Magento → Integrations",
    steps: [
      "Magento Admin → System → Extensions → Integrations → Add New Integration.",
      "Name: \"HelloSir.ai\". Email: yours. Save.",
      "Click API → grant: Catalog (Inventory, Products), Stores (Settings).",
      "Save → click Activate on the integration row.",
      "Copy the Access Token. Paste it below (no \"Bearer\" prefix needed).",
    ],
    note: "Token never expires unless revoked. Requires admin permissions. Magento 2.3+ recommended.",
  },
  bigcommerce: {
    title: "How to get your BigCommerce Storefront token",
    admin_path: "/manage/settings/api-accounts",
    admin_label: "Open BigCommerce → API accounts",
    steps: [
      "BigCommerce Control Panel → Settings → API Accounts → Create API Account → Create Storefront API Token.",
      "Name: \"HelloSir.ai\". Allowed CORS origins: leave blank or your website domain.",
      "Channel: select your storefront. Expires: pick a far date.",
      "Save → copy the JWT token shown.",
      "Store Hash is the value after \"stores/\" in your control-panel URL (e.g. abc123).",
    ],
    note: "Storefront API is read-only and public-safe. Required for live product search.",
  },
};

const PLATFORMS = [
  { id: "wordpress", label: "WordPress / WooCommerce", api: true, fields: [
    { key: "consumer_key", label: "Consumer Key", placeholder: "ck_..." },
    { key: "consumer_secret", label: "Consumer Secret", placeholder: "cs_...", type: "password" },
  ]},
  { id: "shopify", label: "Shopify", api: true, fields: [
    { key: "storefront_token", label: "Storefront API token", placeholder: "shpat_...", type: "password" },
  ]},
  { id: "magento", label: "Magento 2", api: true, fields: [
    { key: "admin_token", label: "Integration access token", placeholder: "eyJ… or 32-char token", type: "password" },
  ]},
  { id: "bigcommerce", label: "BigCommerce", api: true, fields: [
    { key: "store_hash", label: "Store hash", placeholder: "abc123" },
    { key: "storefront_token", label: "Storefront token", placeholder: "JWT …", type: "password" },
  ]},
  { id: "php", label: "PHP (custom)", api: false },
  { id: "nodejs", label: "Node.js / Next.js", api: false },
  { id: "static", label: "Static HTML", api: false },
  { id: "wix", label: "Wix", api: false },
  { id: "squarespace", label: "Squarespace", api: false },
  { id: "other", label: "Other", api: false },
];

const STEP = { SITE_TYPE: 1, PLATFORM: 2, CREDENTIALS: 3, DONE: 4 };

export default function EcomWizard({ open, onClose, initialUrl = "", onSaved, agentId }) {
  const [step, setStep] = useState(STEP.SITE_TYPE);
  const [siteType, setSiteType] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [storeUrl, setStoreUrl] = useState(initialUrl);
  const [creds, setCreds] = useState({});
  const [saving, setSaving] = useState(false);
  const [helpOpen, setHelpOpen] = useState(true);

  // Keep storeUrl in sync if the wizard is reopened with a new initial URL
  React.useEffect(() => { if (open) setStoreUrl(initialUrl || ""); /* eslint-disable-next-line */ }, [open, initialUrl]);

  if (!open) return null;

  const reset = () => {
    setStep(STEP.SITE_TYPE); setSiteType(null); setPlatform(null); setCreds({}); setHelpOpen(true);
  };
  const handleClose = () => { reset(); onClose(); };

  const handleSiteType = (type) => {
    setSiteType(type);
    if (type === "static") {
      setPlatform("static");
      // Skip platform pick, save and finish
      saveConfig({ site_type: "static", platform: "static", store_url: storeUrl });
    } else {
      setStep(STEP.PLATFORM);
    }
  };

  const handlePlatform = (p) => {
    setPlatform(p);
    const meta = PLATFORMS.find((x) => x.id === p);
    if (meta?.api && meta.fields) {
      setStep(STEP.CREDENTIALS);
    } else {
      saveConfig({ site_type: "ecommerce", platform: p, store_url: storeUrl });
    }
  };

  const saveConfig = async (payload) => {
    setSaving(true);
    try {
      const { data } = await api.post("/ecommerce/config", payload, { params: { agent_id: agentId } });
      toast.success("Store settings saved");
      onSaved?.(data);
      handleClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const saveWithCreds = () => {
    const payload = { site_type: "ecommerce", platform, store_url: storeUrl, ...creds };
    saveConfig(payload);
  };

  const skipCreds = () => {
    saveConfig({ site_type: "ecommerce", platform, store_url: storeUrl });
  };

  const meta = PLATFORMS.find((p) => p.id === platform);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#030712]/60 backdrop-blur-sm p-4" data-testid="ecom-wizard">
      <div className="bg-white max-w-2xl w-full rounded-sm shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#030712]">
          <div className="flex items-center gap-2 font-display font-extrabold text-base uppercase tracking-tight">
            <Zap className="w-4 h-4 text-[#0044FF]" /> Smart training wizard
          </div>
          <button onClick={handleClose} className="text-[#4B5563] hover:text-[#030712]" data-testid="wizard-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 overflow-auto">
          {/* Always-visible store URL input — required for product scanning */}
          <div className="mb-5">
            <label className="text-[10px] font-mono uppercase tracking-wide text-[#4B5563]">Your website / store URL</label>
            <input
              data-testid="wizard-store-url"
              type="text"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              placeholder="https://hishoppers.com"
              className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-xs font-mono mt-1"
            />
            <div className="text-[10px] text-[#4B5563] mt-1">We&apos;ll add https:// automatically if missing.</div>
          </div>
          {step === STEP.SITE_TYPE && (
            <>
              <div className="text-sm text-[#4B5563] mb-4">
                We&apos;re about to train your agent from <span className="font-mono font-bold text-[#030712] break-all">{storeUrl}</span>.
                What kind of site is this?
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button data-testid="wizard-site-static" onClick={() => handleSiteType("static")} className="hs-card p-5 hover:border-[#0044FF] text-left">
                  <Globe className="w-6 h-6 text-[#030712] mb-2" />
                  <div className="font-display font-bold text-sm">Static website</div>
                  <div className="text-[11px] text-[#4B5563] mt-1">Marketing site, blog, portfolio, SaaS landing — anything that&apos;s not a store.</div>
                </button>
                <button data-testid="wizard-site-ecommerce" onClick={() => handleSiteType("ecommerce")} className="hs-card p-5 hover:border-[#0044FF] text-left">
                  <ShoppingBag className="w-6 h-6 text-[#0044FF] mb-2" />
                  <div className="font-display font-bold text-sm">eCommerce store</div>
                  <div className="text-[11px] text-[#4B5563] mt-1">Sells products — we&apos;ll scan your catalog (prices, images, stock).</div>
                </button>
              </div>
            </>
          )}

          {step === STEP.PLATFORM && (
            <>
              <div className="text-sm text-[#4B5563] mb-4">Which platform powers your store?</div>
              <div className="grid grid-cols-2 gap-2 max-h-96 overflow-auto">
                {PLATFORMS.map((p) => (
                  <button key={p.id} data-testid={`wizard-platform-${p.id}`} onClick={() => handlePlatform(p.id)} className="hs-card p-3 hover:border-[#0044FF] text-left text-xs">
                    <div className="font-display font-bold text-sm">{p.label}</div>
                    <div className="text-[10px] font-mono text-[#4B5563] mt-1">{p.api ? "Native API + Universal scan" : "Universal sitemap scan"}</div>
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(STEP.SITE_TYPE)} className="text-[11px] text-[#4B5563] mt-3 hover:text-[#030712]">← Back</button>
            </>
          )}

          {step === STEP.CREDENTIALS && meta?.fields && (
            <>
              <div className="text-sm text-[#4B5563] mb-3">
                Optional: connect <span className="font-bold">{meta.label}</span> API for live product data.
                <span className="block text-[11px] mt-1">Skip and we&apos;ll still scan your catalog via sitemap.</span>
              </div>

              {/* In-context help: how to get the keys */}
              {HELP[platform] && (
                <div className="border-2 border-[#0044FF]/20 bg-[#0044FF]/5 mb-4 rounded-sm" data-testid="wizard-help-block">
                  <button
                    type="button"
                    onClick={() => setHelpOpen(!helpOpen)}
                    className="w-full flex items-center justify-between p-3 text-left"
                    data-testid="wizard-help-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-4 h-4 text-[#0044FF]" />
                      <span className="font-display font-bold text-xs uppercase tracking-tight">{HELP[platform].title}</span>
                    </div>
                    {helpOpen ? <ChevronDown className="w-4 h-4 text-[#0044FF]" /> : <ChevronRight className="w-4 h-4 text-[#0044FF]" />}
                  </button>
                  {helpOpen && (
                    <div className="px-3 pb-3 space-y-2">
                      <ol className="text-[11px] text-[#030712] space-y-1.5 list-decimal pl-4 leading-relaxed">
                        {HELP[platform].steps.map((s, i) => <li key={i}>{s}</li>)}
                      </ol>
                      {HELP[platform].note && (
                        <div className="text-[10px] text-[#4B5563] italic border-l-2 border-[#0044FF] pl-2 mt-2">{HELP[platform].note}</div>
                      )}
                      {storeUrl && HELP[platform].admin_path && (
                        <a
                          href={`${storeUrl.replace(/\/$/, "")}${HELP[platform].admin_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-[#0044FF] hover:underline mt-2"
                          data-testid="wizard-open-admin"
                        >
                          <ExternalLink className="w-3 h-3" /> {HELP[platform].admin_label} ↗
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {meta.fields.map((f) => (
                  <div key={f.key}>
                    <label className="text-[10px] font-mono uppercase text-[#4B5563]">{f.label}</label>
                    <input
                      type={f.type || "text"}
                      placeholder={f.placeholder}
                      value={creds[f.key] || ""}
                      onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                      className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-xs font-mono"
                      data-testid={`wizard-cred-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={skipCreds} disabled={saving} className="btn-outline text-xs flex-1" data-testid="wizard-skip-creds">Skip for now</button>
                <button onClick={saveWithCreds} disabled={saving} className="btn-primary text-xs flex-1" data-testid="wizard-save-creds">{saving ? "Saving…" : "Connect & save"}</button>
              </div>
              <button onClick={() => setStep(STEP.PLATFORM)} className="text-[11px] text-[#4B5563] mt-3 hover:text-[#030712]">← Back</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
